"use client";

import React from "react";
import Image from "next/image";
import { FaCode } from "react-icons/fa";

// Helper to render FontAwesome icon or image
const RenderIcon = ({ iconStr, className }) => {
  if (!iconStr) return <FaCode className={className} />;
  
  const isUrl = (typeof iconStr === 'string') && (iconStr.startsWith('http') || iconStr.startsWith('/') || iconStr.includes('uploads'));
  
  if (isUrl) {
    return <Image src={iconStr} alt="Technical Expertise Icon" className={className + " object-contain"} width={24} height={24} />;
  }

  // Ensure FontAwesome classes are correct (e.g. "fa-solid fa-code")
  const iconClass = (typeof iconStr === 'string' && iconStr.includes(' ')) ? iconStr : `fa-solid fa-${iconStr}`;
  return <i className={`${iconClass} ${className}`}></i>;
};

export default function CoreExpertise({ techLabel, expertise }) {
  // Static fallback capabilities
  const defaultCapabilities = [
    {
      icon: "code",
      title: "Custom Development",
      description: `End-to-end tailored software architectures engineered specifically for enterprise-grade ${techLabel} protocols.`
    },
    {
      icon: "server",
      title: "Cloud Integration",
      description: `Robust deployment cycles mapped uniformly across high-capacity multi-tenant server infrastructure models.`
    },
    {
      icon: "sync",
      title: "Realtime Synchronization",
      description: `Continuous state transfer architectures maintaining sub-millisecond operational data parity targets.`
    },
    {
      icon: "mobile",
      title: "Omni-Channel Delivery",
      description: `Seamlessly adapted visual presentations scaling efficiently across cross-platform hardware specifications.`
    }
  ];

  const title = expertise?.title || `Our Technical Capabilities in ${techLabel}`;
  const description = expertise?.description || "Leverage our full-cycle specialization to build performant digital solutions optimized securely.";
  
  // Use backend cards if they exist, otherwise use default
  const rawCards = expertise?.cards?.filter(c => c.title) || [];
  const capabilities = rawCards.length > 0 ? rawCards : defaultCapabilities;

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto relative z-10">

      {/* Section Title */}
      <div className="text-center mb-16">
        <span className="text-xs font-bold tracking-widest text-[#f48227] uppercase bg-transparent px-4 py-1.5 rounded-full" style={{ border: "1px solid #f48227" }}>
          Expertise
        </span>
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-white leading-tight mt-4 mb-4">
          {title}
        </h2>
        <p className="text-gray-400 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
          {description}
        </p>
      </div>

      {/* Capability Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {capabilities.map((cap, i) => {
          return (
            <div
              key={i}
              style={{ backgroundColor: "#111010d1" }}
              className="p-8 md:p-10 rounded-[2rem] flex flex-col sm:flex-row gap-6 items-start transition-all duration-500 hover:-translate-y-1 ring-2 ring-zinc-800 shadow-xl shadow-black/20 group"
            >
              {/* Icon container */}
              <div className="w-14 h-14 rounded-2xl bg-[#f1833b]/10 ring-1 ring-[#f1833b]/20 flex items-center justify-center text-[#f1833b] flex-shrink-0 group-hover:bg-[#f1833b] group-hover:text-black transition-all duration-500 shadow-inner">
                <RenderIcon iconStr={typeof cap.icon === 'string' ? cap.icon : null} className="text-2xl" />
              </div>

              {/* Content */}
              <div className="space-y-2">
                <h3 className="text-lg font-bold text-white group-hover:text-[#f1833b] transition-colors duration-300">
                  {cap.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {cap.description || cap.desc}
                </p>
              </div>
            </div>
          );
        })}
      </div>

    </section>
  );
}
